import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = '7 May 2026';
const CONTROLLER   = 'ImagiSoft Ltd';
const CONTACT      = 'privacy@imagisoft.co.uk';
const APP_NAME     = 'Win/R';
const WEBSITE      = 'https://win.imagisoft.co.uk';

const sections = [
  {
    id: 'overview',
    title: '1. Who We Are',
    body: `${CONTROLLER} ("we", "us", "our") operates the ${APP_NAME} personal training management platform, available at ${WEBSITE} and as a mobile application (collectively the "Service").

We are registered in England and Wales. For the purposes of UK GDPR and the Data Protection Act 2018, ${CONTROLLER} is the data controller. For users in the United States, we act as a business under the California Consumer Privacy Act (CCPA) and other applicable US state privacy laws.

Contact our privacy team at any time: ${CONTACT}`
  },
  {
    id: 'data-collected',
    title: '2. Data We Collect',
    body: `We collect the following categories of personal data:

Account & Identity
• Full name and email address
• Encrypted password (we never store plain-text passwords)
• Business name and website (trainers)
• Profile photograph (optional)

Health & Fitness Data
• Body measurements (weight, body fat %, height and custom metrics)
• Workout logs, session notes, and progress records
• Nutrition plans associated with your account

Financial Data
• Invoice amounts, payment status, and due dates
• We do not store full card numbers — payments are handled by third-party processors

Communications & Content
• Posts, comments, and media you share in the community feed
• Direct messages between trainers and clients
• Videos linked to training programmes

Device & Technical Data
• IP address, browser type, operating system
• Mobile device identifiers (for push notifications)
• App usage data and error logs

We do not knowingly collect data from children under 13 (US) or 13 (UK). If you believe a child has provided us data, contact ${CONTACT} immediately.`
  },
  {
    id: 'lawful-basis',
    title: '3. Lawful Basis for Processing (UK / EU)',
    body: `Under UK GDPR we rely on the following lawful bases:

• Contract (Article 6(1)(b)): processing your account data, health metrics, sessions, invoices, and communications is necessary to deliver the Service you have signed up for.

• Legitimate Interests (Article 6(1)(f)): security monitoring, fraud prevention, product analytics, and service improvement — balanced against your rights.

• Consent (Article 6(1)(a)): push notifications and optional marketing communications. You may withdraw consent at any time in Settings.

• Legal Obligation (Article 6(1)(c)): retaining financial records as required by UK tax law (HMRC).

Health and fitness data constitutes special category data under Article 9. We process it on the basis of your explicit consent (Article 9(2)(a)), obtained via a dedicated consent checkbox presented at account creation. You may withdraw this consent at any time by contacting us at privacy@imagisoft.co.uk, though withdrawal may affect our ability to deliver core Service features that depend on this data.`
  },
  {
    id: 'how-we-use',
    title: '4. How We Use Your Data',
    body: `We use your personal data to:

• Create and manage your account
• Deliver core features: client management, session logging, programme and nutrition plan creation, invoicing, scheduling, and the community feed
• Send transactional notifications (session reminders, invoice alerts, new messages)
• Improve the Service through aggregated, anonymised analytics
• Investigate and prevent fraud, abuse, and security incidents
• Meet legal and regulatory obligations

We do not sell your personal data to third parties. We do not use your health or fitness data for advertising.`
  },
  {
    id: 'sharing',
    title: '5. Who We Share Data With',
    body: `We share data only with trusted processors under written data processing agreements:

Infrastructure
• Amazon Web Services (AWS) — servers and file storage (regions: eu-west-1 for UK users; us-east-1 for US users)

Notifications
• Firebase (Google LLC) — push notification delivery

Subscription Billing
• RevenueCat Inc — subscription lifecycle management and entitlement tracking (no payment card data is shared; card processing is handled entirely by Apple or Google)

Payments
• Stripe Inc — payment processing for trainer–client transactions. Stripe receives client name, email, and payment card details directly. We do not store full card numbers.

App Stores & Device Platforms
• Apple Inc (App Store, StoreKit) — in-app purchase processing for iOS subscribers; subject to Apple's Privacy Policy
• Google LLC (Google Play, Billing Library) — in-app purchase processing for Android subscribers; subject to Google's Privacy Policy

Legal & Compliance
• Law enforcement or regulators when required by applicable law, court order, or to protect the rights and safety of users

We never sell, rent, or trade your personal data to data brokers or advertisers.`
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    body: `We keep your data for as long as your account is active. On account deletion:

• Account and profile data — deleted within 30 days
• Health and fitness records — deleted within 30 days
• Community posts — deleted or anonymised within 30 days
• Financial records (invoices) — retained for 7 years to comply with UK and US tax obligations
• Anonymised, aggregated analytics — retained indefinitely (cannot identify you)

You may request deletion at any time (see Your Rights below).`
  },
  {
    id: 'uk-rights',
    title: '7. Your Rights — UK & EU (UK GDPR)',
    body: `If you are in the UK or EU, you have the following rights:

• Access: request a copy of the personal data we hold about you
• Rectification: correct inaccurate or incomplete data
• Erasure ("right to be forgotten"): request deletion of your data, subject to legal retention obligations
• Restriction: ask us to pause processing while a dispute is resolved
• Portability: receive your data in a structured, machine-readable format
• Objection: object to processing based on legitimate interests
• Withdraw Consent: for any processing based on consent (e.g. push notifications)
• Automated Decision-Making: we do not make solely automated decisions with legal or significant effects

To exercise any right, email ${CONTACT}. We will respond within 30 days. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk or by calling 0303 123 1113.`
  },
  {
    id: 'us-rights',
    title: '8. Your Rights — United States (CCPA / State Privacy Laws)',
    body: `If you are a California resident (or resident of a US state with applicable privacy law), you have the right to:

• Know: request disclosure of the categories and specific pieces of personal information we have collected about you in the past 12 months
• Delete: request deletion of your personal information, subject to certain exceptions
• Correct: request correction of inaccurate personal information
• Opt Out of Sale or Sharing: we do not sell or share personal information for cross-context behavioural advertising — no opt-out action is required
• Limit Use of Sensitive Personal Information (SPI): health and fitness data qualifies as SPI under CPRA. We use it solely to provide the Service and do not disclose it to third parties for any other purpose. To request further limitation, email ${CONTACT}.
• Non-Discrimination: we will not discriminate against you for exercising your privacy rights

To submit a request, email ${CONTACT} with subject line "US Privacy Rights Request". We will verify your identity and respond within 45 days (extendable by a further 45 days with notice).

Other US State Residents: If you reside in Virginia (VCDPA), Colorado (CPA), Connecticut (CTDPA), Texas, or another US state with applicable privacy legislation, you have similar rights to access, correct, delete, and opt out of certain processing. The same contact email and 45-day response commitment apply. We do not engage in profiling that produces legal or similarly significant effects.

Sensitive personal information (health and fitness data) is collected only to provide the Service and is not used for inferring characteristics about you or shared with third parties for advertising.`
  },
  {
    id: 'security',
    title: '9. Security',
    body: `We implement appropriate technical and organisational measures to protect your data:

• All data in transit is encrypted via TLS 1.2+ (HTTPS)
• Passwords are hashed using bcrypt with per-user salts
• Authentication tokens are stored in device secure storage (iOS Keychain / Android Keystore)
• AWS infrastructure uses encrypted storage and restricted access controls
• We conduct regular security reviews of our codebase and infrastructure

No system is perfectly secure. If you discover a vulnerability, please report it responsibly to ${CONTACT}.`
  },
  {
    id: 'international',
    title: '10. International Transfers',
    body: `If you are in the UK or EU, your data may be transferred to and processed in the United States (by AWS and Firebase). These transfers are conducted under:

• AWS: UK Addendum to the EU Standard Contractual Clauses
• Firebase / Google: Google's Data Processing Terms incorporating Standard Contractual Clauses

You may request a copy of the relevant safeguards by contacting ${CONTACT}.`
  },
  {
    id: 'cookies',
    title: '11. Cookies & Local Storage',
    body: `The web application uses browser localStorage and sessionStorage to maintain your login session and user preferences. No third-party tracking cookies are placed on your device.

The mobile application uses secure device storage (expo-secure-store) for authentication tokens and does not use browser cookies.`
  },
  {
    id: 'changes',
    title: '12. Changes to This Policy',
    body: `We may update this Privacy Policy from time to time. When we make material changes we will notify you by email or prominent in-app notice at least 14 days before the change takes effect. The "Last Updated" date at the top of this page reflects the most recent revision.

Continued use of the Service after the effective date constitutes acceptance of the updated policy.`
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    body: `For any privacy-related queries, requests, or complaints:

Email:   ${CONTACT}
Post:    ${CONTROLLER}, c/o Privacy Team, imagisoft.co.uk
Website: https://www.imagisoft.co.uk

For UK users, you may also contact the ICO:
Website: ico.org.uk
Phone:   0303 123 1113`
  },
];

export default function PrivacyPolicy({ standalone = false }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header">
        <button className="icon-btn" style={{ marginRight: 4 }} onClick={() => navigate(-1)}>
          <span style={{ fontSize: 22, color: 'var(--color-text)' }}>‹</span>
        </button>
        <h1 style={{ flex: 1 }}>Privacy Policy</h1>
      </div>

      <div className="page-content" style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* Header card */}
        <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--color-primary)' }}>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 6 }}>Last updated: {LAST_UPDATED}</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--color-text)' }}>
            This policy explains what personal data <strong>{CONTROLLER}</strong> collects when you use <strong>{APP_NAME}</strong>, how we use it, and the rights available to you under UK GDPR, the Data Protection Act 2018, and the California Consumer Privacy Act (CCPA).
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['🇬🇧 UK GDPR Compliant', '🇺🇸 CCPA Compliant', '🔒 TLS Encrypted'].map(badge => (
              <span key={badge} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 12, background: 'rgba(215,38,56,0.1)', color: 'var(--color-primary)', border: '1px solid rgba(215,38,56,0.2)' }}>
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Sections */}
        {sections.map(sec => (
          <div key={sec.id} className="card" style={{ marginBottom: 10 }}>
            <button
              onClick={() => setActiveSection(activeSection === sec.id ? null : sec.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}>
              <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--color-text)' }}>{sec.title}</span>
              <span style={{ fontSize: 18, color: 'var(--color-text-muted)', transition: 'transform 0.2s', transform: activeSection === sec.id ? 'rotate(180deg)' : 'none' }}>
                ›
              </span>
            </button>

            {activeSection === sec.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border)', fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.75, whiteSpace: 'pre-line' }}>
                {sec.body}
              </div>
            )}
          </div>
        ))}

        {/* Contact footer */}
        <div style={{ textAlign: 'center', padding: '24px 0 40px', fontSize: 13, color: 'var(--color-text-muted)' }}>
          Questions? Email us at{' '}
          <a href={`mailto:${CONTACT}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{CONTACT}</a>
        </div>
      </div>
    </div>
  );
}
